/**
 * orval mutator backing every generated API call (see orval.config.ts).
 *
 * Replaces orval's default fetch client body handling, which JSON.parses
 * every non-empty body unguarded — a plain-text 500 ("Internal Server
 * Error") used to surface to operators as `Unexpected token 'I', …`.
 *
 * Contract (matches the callers in api-client/queries.ts and pages):
 * resolves with `{ data, status, headers }` for every response; never
 * throws on an unparseable body. Non-JSON bodies become
 * `{ detail: FRIENDLY_FALLBACK_MESSAGE }` so status checks downstream
 * always find a human-readable `detail`.
 */
import { FRIENDLY_FALLBACK_MESSAGE } from "@/lib/api-error";

const BODYLESS_STATUSES = [204, 205, 304];

export async function customFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  const bodyText = BODYLESS_STATUSES.includes(response.status) ? null : await response.text();

  let data: unknown = null;
  if (bodyText) {
    try {
      data = JSON.parse(bodyText);
    } catch {
      // Non-JSON body (plain-text 500, proxy HTML, …) — never leak it.
      data = { detail: FRIENDLY_FALLBACK_MESSAGE };
    }
  } else if (bodyText === "") {
    data = {};
  }

  return { data, status: response.status, headers: response.headers } as T;
}

export default customFetch;
